import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaMoneyBillWave, FaHistory, FaFileInvoice, FaExternalLinkAlt, FaEye } from "react-icons/fa";
import Sidebar from "../Sidebar/Sidebar";
import AdminNavbar from "../Navbars/AdminNavbar.js";
import { SmsWalletApis } from "../../apis/smsApis";

const TYPE_COLORS: Record<string, string> = {
  deposit:         'bg-green-100 text-green-800',
  withdrawal:      'bg-red-100 text-red-800',
  message_payment: 'bg-blue-100 text-blue-800',
  refund:          'bg-yellow-100 text-yellow-800',
};

const STATUS_COLORS: Record<string, string> = {
  completed: 'bg-green-100 text-green-800',
  pending:   'bg-yellow-100 text-yellow-800',
  failed:    'bg-red-100 text-red-800',
  reversed:  'bg-purple-100 text-purple-800',
};

function Badge({ label, colorMap }: { label: string; colorMap: Record<string, string> }) {
  const cls = colorMap[label.toLowerCase()] ?? 'bg-gray-100 text-gray-800';
  return (
    <span className={`px-2 py-1 text-xs font-semibold rounded-full capitalize ${cls}`}>
      {label.replace(/_/g, ' ')}
    </span>
  );
}

function NativeModal({ open, onClose, title, children, width = 'max-w-md' }: {
  open: boolean; onClose: () => void; title: string; children: React.ReactNode; width?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${width} max-h-[90vh] flex flex-col`}>
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-bold text-gray-800">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">&times;</button>
        </div>
        <div className="overflow-y-auto flex-1 px-6 py-4">{children}</div>
      </div>
    </div>
  );
}

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(n);

export default function SmsWallet() {
  const [wallet, setWallet] = useState<any>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [transactions, setTransactions] = useState<any>([]);
  const [txLoading, setTxLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const perPage = 10;

  const [fundOpen, setFundOpen] = useState(false);
  const [fundAmount, setFundAmount] = useState("");
  const [fundingLoading, setFundingLoading] = useState(false);

  const [paymentLinkOpen, setPaymentLinkOpen] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");

  const [txDetailOpen, setTxDetailOpen] = useState(false);
  const [selectedTx, setSelectedTx] = useState<any>(null);

  useEffect(() => { fetchWallet(); }, []);
  useEffect(() => { fetchTransactions(); }, [currentPage, typeFilter, statusFilter]);

  const fetchWallet = async () => {
    setWalletLoading(true);
    try {
      const res = await SmsWalletApis.getWalletDetails();
      if (res?.data?.wallet) setWallet(res.data.wallet);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to fetch wallet");
    } finally { setWalletLoading(false); }
  };

  const fetchTransactions = async () => {
    setTxLoading(true);
    try {
      const res = await SmsWalletApis.getTransactions(perPage, typeFilter, statusFilter);
      if (res?.data?.data) {
        setTransactions(res.data.data);
        setTotalPages(Math.ceil(res.data.data.total / res.data.data.per_page) || 1);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to fetch transactions");
    } finally { setTxLoading(false); }
  };

  const handleFundWallet = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(fundAmount);
    if (isNaN(amount) || amount < 1000) { toast.error("Minimum amount is ₦1,000"); return; }
    setFundingLoading(true);
    try {
      const res = await SmsWalletApis.initiatePayment({
        amount, currency: "NGN", description: "SMS Wallet Top-up",
        redirect_url: `${window.location.origin}/verify-payment`,
      });
      if (res?.data?.payment_link) {
        setPaymentLink(res.data.payment_link);
        setFundOpen(false); setFundAmount("");
        setPaymentLinkOpen(true);
      } else {
        toast.error("Failed to initiate payment");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to initiate payment");
    } finally { setFundingLoading(false); }
  };

  const generateInvoice = async (id: number) => {
    try {
      const res = await SmsWalletApis.generateInvoice(id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.setAttribute('download', `invoice-${id}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Invoice downloaded");
    } catch { toast.error("Failed to generate invoice"); }
  };

  const txRows: any[] = transactions?.data ?? [];

  return (
    <>
      <Sidebar title="SMS Wallet" />
      <div className="relative md:ml-64 bg-gray-50 min-h-screen">
        <AdminNavbar title="" />
        <div className="px-4 md:px-10 mx-auto w-full py-8">

          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">SMS Wallet</h1>
            <p className="text-sm text-gray-500 mt-1">Manage your SMS credit balance and transaction history</p>
          </div>

          {/* Wallet overview */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            {walletLoading ? (
              <div className="flex justify-center py-6">
                <div className="w-8 h-8 border-4 border-[#0071BC] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-100 rounded-full">
                      <FaMoneyBillWave className="text-blue-600 text-lg" />
                    </div>
                    <span className="text-sm text-gray-600">Current Balance</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">{wallet ? fmtCurrency(wallet.balance) : '₦0.00'}</p>
                  <p className="text-xs text-gray-400 mt-1">Available for sending messages</p>
                </div>

                <div className="bg-green-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-green-100 rounded-full">
                      <FaHistory className="text-green-600 text-lg" />
                    </div>
                    <span className="text-sm text-gray-600">Messages Available</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800">
                    {wallet ? Math.floor(wallet.balance / 5.99).toLocaleString() : '0'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">Approx. at ₦5.99/segment</p>
                </div>

                <div className="flex items-center justify-center md:justify-end">
                  <button
                    onClick={() => { setFundAmount(""); setFundOpen(true); }}
                    className="bg-[#0071BC] hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
                  >
                    Fund Wallet
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Transaction history */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between px-6 py-4 border-b gap-3">
              <h2 className="text-base font-semibold text-gray-800">Transaction History</h2>
              <div className="flex gap-2">
                <select
                  className="border border-gray-300 text-gray-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
                  value={typeFilter}
                  onChange={e => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="message_payment">Message Payment</option>
                  <option value="withdrawal">Withdrawal</option>
                  <option value="refund">Refund</option>
                </select>
                <select
                  className="border border-gray-300 text-gray-600 text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="">All Statuses</option>
                  <option value="completed">Completed</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left text-gray-600">
                <thead className="text-xs uppercase bg-gray-50 text-gray-500 border-b">
                  <tr>
                    <th className="px-6 py-4">Reference</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Amount</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {txLoading ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center text-gray-400">
                        <div className="flex justify-center items-center gap-2">
                          <div className="w-5 h-5 border-2 border-[#0071BC] border-t-transparent rounded-full animate-spin" />
                          Loading transactions…
                        </div>
                      </td>
                    </tr>
                  ) : txRows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-16 text-center text-gray-400">
                        <p className="font-medium">No transactions yet</p>
                        <p className="text-xs mt-1">Fund your wallet to get started</p>
                      </td>
                    </tr>
                  ) : txRows.map((tx: any) => (
                    <tr key={tx.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs text-gray-500">{tx.reference?.substring(0, 8)}…</td>
                      <td className="px-6 py-4"><Badge label={tx.type} colorMap={TYPE_COLORS} /></td>
                      <td className="px-6 py-4 font-semibold text-gray-800">{fmtCurrency(tx.amount)}</td>
                      <td className="px-6 py-4"><Badge label={tx.status} colorMap={STATUS_COLORS} /></td>
                      <td className="px-6 py-4">{new Date(tx.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button onClick={() => { setSelectedTx(tx); setTxDetailOpen(true); }}
                            className="text-[#0071BC] hover:text-blue-800" title="View Details">
                            <FaEye />
                          </button>
                          {tx.status === 'completed' && (
                            <button onClick={() => generateInvoice(tx.id)}
                              className="text-green-600 hover:text-green-800" title="Download Invoice">
                              <FaFileInvoice />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!txLoading && txRows.length > 0 && totalPages > 1 && (
              <div className="px-6 py-4 border-t flex items-center justify-between text-sm text-gray-600">
                <span>Page {currentPage} of {totalPages}</span>
                <div className="flex gap-2">
                  <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                    ‹ Prev
                  </button>
                  <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed">
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fund Wallet Modal */}
      <NativeModal open={fundOpen} onClose={() => setFundOpen(false)} title="Fund SMS Wallet">
        <form onSubmit={handleFundWallet} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Amount (NGN)</label>
            <input
              type="number" min="1000" step="100"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0071BC]"
              placeholder="Minimum: ₦1,000"
              value={fundAmount} onChange={e => setFundAmount(e.target.value)} required
            />
            <p className="text-xs text-gray-400 mt-1">Minimum funding amount is ₦1,000</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 border">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="radio" checked readOnly />
              Flutterwave (Card, Bank Transfer, USSD)
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setFundOpen(false)} disabled={fundingLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
              Cancel
            </button>
            <button type="submit" disabled={fundingLoading}
              className="flex-1 px-4 py-2.5 bg-[#0071BC] text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
              {fundingLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {fundingLoading ? 'Processing…' : 'Proceed to Payment'}
            </button>
          </div>
        </form>
      </NativeModal>

      {/* Payment Link Modal */}
      <NativeModal open={paymentLinkOpen} onClose={() => setPaymentLinkOpen(false)} title="Payment Initiated">
        <div className="text-center py-4">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-600 mb-6">Your payment has been initiated. Click below to complete it.</p>
          <a
            href={paymentLink} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#0071BC] hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <FaExternalLinkAlt /> Proceed to Payment
          </a>
          <p className="text-xs text-gray-400 mt-4">Your wallet will be credited automatically once payment is confirmed.</p>
        </div>
      </NativeModal>

      {/* Transaction Detail Modal */}
      <NativeModal open={txDetailOpen} onClose={() => setTxDetailOpen(false)} title="Transaction Details" width="max-w-lg">
        {selectedTx && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              {([
                ['Reference', <span className="font-mono text-xs">{selectedTx.reference}</span>],
                ['Amount', <span className="font-bold text-[#0071BC]">{fmtCurrency(selectedTx.amount)}</span>],
                ...(Number(selectedTx.vat) > 0 ? [
                  ['VAT (7.5%)', fmtCurrency(Number(selectedTx.vat))],
                  ['Net Credit', fmtCurrency(Number(selectedTx.net_amount))],
                ] : []),
                ['Type',   <Badge label={selectedTx.type}   colorMap={TYPE_COLORS} />],
                ['Status', <Badge label={selectedTx.status} colorMap={STATUS_COLORS} />],
                ['Date',   new Date(selectedTx.created_at).toLocaleString()],
                ...(selectedTx.payment_method ? [['Payment Method', <span className="capitalize">{selectedTx.payment_method}</span>]] : []),
              ] as [string, React.ReactNode][]).map(([label, val]) => (
                <div key={label} className="flex justify-between items-center">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-gray-800">{val}</span>
                </div>
              ))}
            </div>

            {selectedTx.description && (
              <div>
                <p className="text-xs font-semibold text-gray-500 mb-1">DESCRIPTION</p>
                <p className="text-sm text-gray-700">{selectedTx.description}</p>
              </div>
            )}

            {selectedTx.status === 'completed' && (
              <button
                onClick={() => { generateInvoice(selectedTx.id); setTxDetailOpen(false); }}
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2.5 rounded-lg"
              >
                <FaFileInvoice /> Download Invoice
              </button>
            )}
          </div>
        )}
      </NativeModal>

      <ToastContainer position="top-right" autoClose={3000} />
    </>
  );
}
